'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';

enum ModalType {
  Alert,
  Confirm,
  Prompt,
}

export type Modals = {
  openAlertModal: (
    message: string | ReactNode,
    opts?: ModalOpenerProps
  ) => Promise<boolean | null>;
  openConfirmModal: (
    message: string | ReactNode,
    opts?: ModalOpenerProps
  ) => Promise<boolean | null>;
  openPromptModal: (
    message: string | ReactNode,
    opts?: ModalOpenerProps & {
      defaultValue?: string;
    }
  ) => Promise<string | null>;
};

export type ModalOpenerProps = {
  okText?: string;
  cancelText?: string;
  icon?: ReactNode;
  modalProps?: Partial<React.ComponentProps<typeof Modal>>;
  okButtonProps?: Partial<React.ComponentProps<typeof Button>>;
  cancelButtonProps?: Partial<React.ComponentProps<typeof Button>>;
  onCancel?: () => void;
  onSuccess?: (result?: string) => void;
};

const defaultContext: Modals = {
  openAlertModal() {
    throw new Error('<ModalProvider> is missing');
  },
  openConfirmModal() {
    throw new Error('<ModalProvider> is missing');
  },
  openPromptModal() {
    throw new Error('<ModalProvider> is missing');
  },
};

const Context = createContext<Modals>(defaultContext);

interface AnyEvent {
  preventDefault(): void;
}

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modal, setModal] = useState<ReactNode | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const ok = useRef<HTMLButtonElement>(null);

  const createOpener = useCallback(
    (type: ModalType) =>
      (
        message: string,
        opts: ModalOpenerProps & { defaultValue?: string } = {}
      ) =>
        new Promise<boolean | string | undefined>((resolve) => {
          const handleClose = (e?: AnyEvent) => {
            e?.preventDefault();
            setModal(null);
            opts.onCancel?.();
            resolve(undefined);
          };

          const handleCancel = (e?: AnyEvent) => {
            e?.preventDefault();
            setModal(null);
            opts.onCancel?.();
            if (type === ModalType.Prompt) {
              resolve(undefined);
            } else {
              resolve(false);
            }
          };

          const handleOK = (e?: AnyEvent) => {
            e?.preventDefault();
            setModal(null);
            if (type === ModalType.Prompt) {
              const promptValue = input.current?.value;
              opts.onSuccess?.(promptValue);
              resolve(promptValue);
            } else {
              opts.onSuccess?.();
              resolve(true);
            }
          };

          setModal(
            <Modal
              isOpen={true}
              onClose={handleClose}
              initialFocusRef={type === ModalType.Prompt ? input : ok}
              isCentered
              {...opts.modalProps}
            >
              <ModalOverlay bgColor="ae.modalBg" />
              <ModalContent>
                <ModalBody mt={5}>
                  <Flex gap={4} alignItems="center">
                    {opts.icon}
                    <Stack spacing={5}>
                      <Text>{message}</Text>
                      {type === ModalType.Prompt && (
                        <Input ref={input} defaultValue={opts.defaultValue} />
                      )}
                    </Stack>
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  {type !== ModalType.Alert && (
                    <Button
                      mr={3}
                      variant="ghost"
                      onClick={handleCancel}
                      {...opts.cancelButtonProps}
                    >
                      {opts.cancelText ?? 'Cancel'}
                    </Button>
                  )}
                  <Button
                    variant={'primary'}
                    color="ae.green"
                    onClick={handleOK}
                    ref={ok}
                    {...opts.okButtonProps}
                  >
                    {opts.okText ?? 'OK'}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          );
        }),
    []
  );

  return (
    <Context.Provider
      value={
        {
          openAlertModal: createOpener(ModalType.Alert),
          openConfirmModal: createOpener(ModalType.Confirm),
          openPromptModal: createOpener(ModalType.Prompt),
        } as Modals
      }
    >
      {children}
      {modal}
    </Context.Provider>
  );
};

export const useModals = () => useContext(Context);
